// Users/aminb101/iuhimporter/autolistify-initial-working-local/frontend/src/utils/api.js
import { v4 as uuidv4 } from 'uuid';
import { logger, logApiRequest, logApiResponse, logApiError } from './logger';
import { isEmbeddedApp, getShopifySessionToken } from './shopifyAppBridge';

// Base API configuration
const deriveDefaultApi = () => {
  const prodDefault = 'https://api.autolistify.com';

  // 1. In production, ALWAYS use the production API URL
  if (process.env.NODE_ENV === 'production') {
    console.log('[API] Using production API URL:', prodDefault);
    return prodDefault;
  }

  // 2. In development, check for env override
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  // 3. If running on localhost, default to local backend
  try {
    const host = window?.location?.hostname || '';
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return 'http://localhost:3001';
    }
  } catch (_) { }

  // 4. Fallback
  return 'http://localhost:3001';
};

// Ensure no trailing slash
export const API_URL = deriveDefaultApi().replace(/\/$/, '');

// Safely parse JSON, returning null on empty or non-JSON responses
async function parseJsonSafe(response) {
  try {
    if (!response) return null;
    if (response.status === 204) return null; // No Content
    const ct = response.headers.get('content-type') || '';
    if (!ct.toLowerCase().includes('application/json')) {
      // Try to detect empty body even if content-type is wrong
      const text = await response.clone().text();
      return text && text.trim().length ? JSON.parse(text) : null;
    }
    const text = await response.clone().text();
    if (!text || !text.trim().length) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Return JWT token from storage (set on login/signup)
const getAuthToken = () => {
  try {
    return localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

// Get current user ID from storage
const getCurrentUserId = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).id : null;
  } catch (error) {
    logger.error('Failed to parse user data', error);
    return null;
  }
};

/**
 * Enhanced fetch with authentication and logging
 */
export async function fetchWithAuth(url, options = {}) {
  const token = getAuthToken();
  const userId = getCurrentUserId();
  const requestId = uuidv4();

  // Set up headers
  const headers = {
    'Content-Type': 'application/json',
    ...(process.env.NODE_ENV !== 'production' && { 'X-Request-ID': requestId }),
    ...(process.env.NODE_ENV !== 'production' && userId && { 'X-User-ID': userId }),
    ...options.headers,
  };

  // Handle authentication for embedded vs standalone apps
  if (isEmbeddedApp()) {
    try {
      const sessionToken = await getShopifySessionToken();
      // Shopify requires both Authorization header AND X-Shopify-Session-Token header
      headers['Authorization'] = `Bearer ${sessionToken}`;
      headers['X-Shopify-Session-Token'] = sessionToken;
      console.log('[Session Token] Using Shopify session token for embedded app request');
    } catch (error) {
      console.warn('Session token unavailable, using fallback auth:', error.message);
      // Fall back to stored token or skip auth for dev
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // In embedded mode without session token, backend will use dev user
    }
  } else {
    // Use regular JWT token for non-embedded access
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Create request config
  const config = {
    ...options,
    headers,
    // Avoid forcing cookies; JWT is in Authorization header
    credentials: options.credentials || 'same-origin',
  };

  try {
    // Log the request
    logApiRequest({
      method: options.method || 'GET',
      url: `${API_URL}${url}`,
      params: options.params,
      data: options.body,
      headers: {
        ...headers,
        ...(token && { 'Authorization': 'Bearer [REDACTED]' }), // Redact token in logs
      },
    });

    // Make the request
    const response = await fetch(`${API_URL}${url}`, config);

    // Safely parse JSON (handles empty/non-JSON bodies)
    const responseData = await parseJsonSafe(response);
    // Attach parsed body for callers who need it without reparsing
    try { response.parsedBody = responseData; } catch { }

    // Log the response
    logApiResponse({
      ...response,
      config,
      data: responseData,
    });

    // If the response is not ok, throw an error
    if (!response.ok) {
      const error = new Error((responseData && (responseData.error || responseData.message)) || response.statusText || 'API request failed');
      error.response = response;
      error.config = config;
      error.data = responseData;
      throw error;
    }

    return response;
  } catch (error) {
    // Log the error
    logApiError(error);
    throw error; // Re-throw to allow error handling by the caller
  }
}

/**
 * API methods for common HTTP methods
 */
export const api = {
  get: (url, options = {}) =>
    fetchWithAuth(url, { ...options, method: 'GET' }),

  post: (url, data, options = {}) =>
    fetchWithAuth(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: (url, data, options = {}) =>
    fetchWithAuth(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (url, options = {}) =>
    fetchWithAuth(url, { ...options, method: 'DELETE' }),

  patch: (url, data, options = {}) =>
    fetchWithAuth(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

/**
 * Initialize API client with user context
 */
export const initApiClient = (user) => {
  if (user) {
    logger.setUser(user);
  }

  // Log API client initialization
  logger.info('API client initialized', {
    apiUrl: API_URL,
    environment: process.env.NODE_ENV,
    userId: user?.id || 'anonymous',
  });
  return api;
};

export default api;